// src/pages/firm/employee/EmployeeList.tsx

import React, { useState } from "react";
import { EDataTable } from "../../../components/EDataTable";
import { IconButton, TextButton } from "../../../components/ButtonComponents";
import { TableActions } from "../../../components/TableActions";
import { Edit, Trash2, UserPlus, ShieldCheck, ShieldAlert } from "lucide-react"; 
import { EmployeeDialog } from "./EmployeeDialog"; 
import { toast } from "react-toastify";

// Tipler ve Hook'lar
import { Employee, EmployeePayload } from "../../../types/employee";  
import { useEmployeeStore } from "../../../store/employeeStore";  
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { useApiErrorHandler } from "../../../hooks/useApiErrorHandler";
import { DataTableColumn, SimpleFilter } from "../../../types"; 

export const EmployeeList = () => {
    const store = useEmployeeStore();
    const { deleteItem, updateItem, createItem } = store; 
    const { handleApiError } = useApiErrorHandler();
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [employeeToEdit, setEmployeeToEdit] = useState<Employee | null>(null);

    const handleNew = () => {
        setEmployeeToEdit(null);
        setIsModalOpen(true);
    };

    const handleEdit = (emp: Employee) => {
        setEmployeeToEdit(emp);
        setIsModalOpen(true);
    };

    const { confirm, modal: confirmModal } = useConfirmModal();

    const handleDeleteClick = async (emp: Employee) => {
        const ok = await confirm(`${emp.firstName} ${emp.lastName} isimli personeli silmek istiyor musunuz?`);
        if (ok) {
            try {
                await deleteItem(emp.id);
                toast.success("Personel silindi");
            } catch (error) {
                handleApiError(error); 
            }
        }
    };

    const handleSaveEmployee = async (data: EmployeePayload) => { 
        try {
            if (employeeToEdit) {
                await updateItem(employeeToEdit.id, data);
                toast.success("Personel güncellendi");
            } else {
                await createItem(data as any);
                toast.success("Yeni personel eklendi");
            }
            setIsModalOpen(false);
        } catch (error) {
            handleApiError(error); 
        }
    };

    const statusBody = (emp: Employee) => (
        <div className="flex items-center gap-1">
            {emp.isActive ? (
                <span className="flex items-center text-green-600 text-sm font-medium">
                    <ShieldCheck className="w-4 h-4 mr-1" /> Aktif
                </span>
            ) : (
                <span className="flex items-center text-red-500 text-sm font-medium">
                    <ShieldAlert className="w-4 h-4 mr-1" /> Pasif
                </span>
            )}
        </div>
    );

    const userMatchBody = (emp: Employee) => (
        <div className="flex items-center gap-2">
            {emp.user ? (
                <div className="bg-blue-50 border border-blue-200 text-blue-700 px-2 py-1 rounded text-xs flex items-center">
                    <UserPlus className="w-3 h-3 mr-1" />
                    {emp.user.name}
                </div>
            ) : (
                <span className="text-gray-400 text-xs italic">Eşleşme Yok</span>
            )}
        </div>
    );

    const actionBody = (emp: Employee) => (
        <TableActions
            actions={[
                {
                    icon: Edit,
                    colorClass: "text-blue-600 hover:bg-blue-50",
                    title: "Düzenle",
                    onClick: () => handleEdit(emp)
                },
                {
                    icon: Trash2,
                    colorClass: "text-red-500 hover:bg-red-50",
                    title: "Sil",
                    onClick: () => handleDeleteClick(emp)
                }
            ]}
        />
    );

    const columns: DataTableColumn<Employee>[] = [
        { field: "externalId", header: "Kod", width: "150px", sortable: true },
        { 
            field: "firstName",
            header: "Ad Soyad", 
            width: "250px",
            body: (emp) => `${emp.firstName} ${emp.lastName}`,
            sortable: true,
        },
        // HATA ÇÖZÜMÜ: Mutlaka field ekliyoruz
        { field: "userId", header: "Bağlı Kullanıcı", body: userMatchBody, width: "250px" },
        { field: "isActive", header: "Durum", body: statusBody, width: "120px" },
        { field: "actions", header: "İşlemler", body: actionBody, width: "120px", align: "center" },
    ];

    return (
        <div className="w-full">
            {confirmModal}
            <EDataTable<Employee> 
            tableKey="employee_list"
                useStore={() => store} 
                dataKey="id"
                title="Personel Listesi"
                placeholder="Ad, Soyad veya Kod Ara"
                searchFields={[
                    { field: "firstName", operator: "contains" },
                    { field: "lastName", operator: "contains" },
                    { field: "externalId", operator: "contains" },
                ]}
                actions={
                    <TextButton onClick={handleNew} className="btn-new">
                         Yeni Personel Ekle
                    </TextButton>
                }
                columns={columns}
            />

            <EmployeeDialog
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                employee={employeeToEdit}
                onSave={handleSaveEmployee}
            />
        </div>
    );
};